import { Calendar, MapPin } from 'lucide-react';
import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import type { HackerVenue } from '@/types';

// The dashboard's location card. One component for both twins (PORTING.md G5): 26-ar
// prints an address block plus a Google Maps button, 26-mx a linked place name.
export function EventLocationCard({
  eventName,
  venue,
  titleFont,
}: {
  eventName: string;
  venue: HackerVenue;
  titleFont: string | null;
}) {
  return (
    <div className="relative overflow-hidden border-2 border-primary bg-background/80 backdrop-blur-sm">
      <div className="absolute inset-0 opacity-40">
        <div
          className="h-full w-full bg-center bg-cover"
          style={{
            backgroundImage: `url(${venue.backgroundImage})`,
            filter: 'grayscale(100%) contrast(130%) brightness(60%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background/30 to-background/60" />
      </div>

      <div className="relative z-10 p-6 sm:p-8 md:p-10">
        <div className="space-y-6">
          {/* Title */}
          <div className="border-primary/20 border-b pb-4">
            <h2
              className={cn(
                'font-bold text-2xl text-primary sm:text-3xl md:text-4xl',
                titleFont,
              )}
            >
              📍 {eventName}
            </h2>
          </div>

          {/* Date and Time */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <h3
                  className={cn(
                    'font-bold text-primary/70 text-sm uppercase',
                    titleFont,
                  )}
                >
                  Fecha y Hora
                </h3>
              </div>
              <div className="border-primary border-l-4 pl-4">
                <p
                  className={cn(
                    'font-bold text-lg text-primary sm:text-xl',
                    titleFont,
                  )}
                >
                  {venue.startsLabel}
                </p>
                <p className="font-mono text-primary/70 text-sm">hasta</p>
                <p
                  className={cn(
                    'font-bold text-lg text-primary sm:text-xl',
                    titleFont,
                  )}
                >
                  {venue.endsLabel}
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <h3
                  className={cn(
                    'font-bold text-primary/70 text-sm uppercase',
                    titleFont,
                  )}
                >
                  Ubicación
                </h3>
              </div>
              <div className="border-primary border-l-4 pl-4">
                {venue.placeUrl ? (
                  <a
                    href={venue.placeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'font-bold text-lg text-primary underline-offset-4 transition-colors hover:underline sm:text-xl',
                      titleFont,
                    )}
                  >
                    {venue.placeName}
                  </a>
                ) : (
                  <p
                    className={cn(
                      'font-bold text-lg text-primary sm:text-xl',
                      titleFont,
                    )}
                  >
                    {venue.placeName}
                  </p>
                )}
                <p className="font-mono text-primary/90 text-sm leading-relaxed">
                  {venue.addressLines.map((line, index) => (
                    <Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </Fragment>
                  ))}
                </p>
              </div>
            </div>
          </div>

          {/* Map Link */}
          {venue.mapUrl && (
            <div className="pt-2">
              <a
                href={venue.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-2 border-2 border-primary bg-primary px-4 py-2 font-bold text-background transition-all hover:bg-background hover:text-primary',
                  titleFont,
                )}
              >
                <MapPin className="h-4 w-4" />
                Ver en Google Maps
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
